import React, { type FunctionComponent, type ReactNode } from 'react'
import styled from '@emotion/styled'
import { Modal, Text, colors, spacing, fontSize } from '@retailer-platform/shared-components'
import { CloseIcon } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'

const FormContainer = styled.div({
  backgroundColor: colors.GRAYSCALE.X0,
  height: '100vh',
  width: '100vw',
  display: 'flex',
  flexDirection: 'column',
})

const FormHeader = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  zIndex: 1,
  height: spacing.X60,
  boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.16)',
})

const CloseButtonContainer = styled.button({
  position: 'absolute',
  left: 0,
  height: fontSize.X18,
  marginLeft: spacing.X20,
  backgroundColor: colors.GRAYSCALE.X0,
  border: 'none',
})

const FormBody = styled.div({
  margin: 'auto 0',
  padding: spacing.X16,
  overflowY: 'auto',
})

const FormFooter = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  zIndex: 1,
  height: spacing.X80,
  boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.16)',
})

interface FullScreenModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  body: ReactNode
  footer: ReactNode
}

export const FullScreenModal: FunctionComponent<FullScreenModalProps> = ({
  isOpen,
  onClose,
  title,
  body,
  footer,
}) => {
  const messages = useDomainMessages({
    close: 'aggregateStorePerformanceDomain.fullScreenModal.close',
  })

  return (
    <Modal
      maxWidth={'100vw'}
      maxHeight={'100vh'}
      onRequestClose={onClose}
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      hideCloseButton
    >
      <FormContainer>
        <FormHeader>
          <CloseButtonContainer onClick={onClose} aria-label={messages.close}>
            <CloseIcon name={messages.close} size={fontSize.X18} />
          </CloseButtonContainer>

          <Text size="large" weight="semibold">
            {title}
          </Text>
        </FormHeader>
        <FormBody>{body}</FormBody>
        <FormFooter>{footer}</FormFooter>
      </FormContainer>
    </Modal>
  )
}
